import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CheckCircle2, XCircle } from "lucide-react";
import * as React from "react";

import {
  acknowledgeDealExpiry,
  closeDeal,
  requestDealExtension,
  type CloseReasonCategory,
  type Deal,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import {
  DealExtensionFields,
  maxExtensionDate,
  minExtensionDate,
} from "@/components/deals/DealExtensionRequestForm";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { dealToasts, useToast } from "@/components/ui/toast";
import { apiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { CLOSE_REASON_LABELS } from "@/lib/deal-labels";

type Path = "extend" | "leave" | "close";

// Order shown in the "Reason for closure" dropdown; labels from the canonical
// CLOSE_REASON_LABELS map. "other" requires a note.
const CLOSE_REASONS: CloseReasonCategory[] = [
  "lost_to_competitor",
  "project_cancelled",
  "no_progress",
  "other",
];

/** DD/MM/YYYY (Australian). */
function formatAuDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

interface Props {
  deal: Deal;
  onClose: () => void;
}

export function DealExpiryDecisionOverlay({ deal, onClose }: Props) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [path, setPath] = React.useState<Path | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Path 1: Extension
  const [requestedDate, setRequestedDate] = React.useState("");
  const [reason, setReason] = React.useState("");

  // Path 3: Close
  const [closeReason, setCloseReason] = React.useState<CloseReasonCategory>("lost_to_competitor");
  const [closeNotes, setCloseNotes] = React.useState("");

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["deals"] });
  };

  const extend = useMutation({
    mutationFn: () =>
      requestDealExtension(accessToken!, deal.id, {
        requested_date: requestedDate,
        justification: reason.trim(),
      }),
    onSuccess: () => {
      toast(dealToasts.extensionRequested());
      invalidate();
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Extension request failed. Refresh and try again.")),
  });

  const acknowledge = useMutation({
    mutationFn: () => acknowledgeDealExpiry(accessToken!, deal.id),
    onSuccess: () => {
      toast(dealToasts.letExpire());
      invalidate();
      onClose();
    },
    onError: () => setError("Acknowledgement failed. Refresh and try again."),
  });

  const close = useMutation({
    mutationFn: () =>
      closeDeal(accessToken!, deal.id, {
        close_reason_category: closeReason,
        close_notes: closeNotes.trim() || null,
      }),
    onSuccess: () => {
      toast(dealToasts.dealClosed());
      invalidate();
      onClose();
    },
    onError: () => setError("Close failed. Refresh and try again."),
  });

  const anyPending = extend.isPending || acknowledge.isPending || close.isPending;

  // "Other" closure requires an accompanying note (CHP-225).
  const closeNoteRequired = path === "close" && closeReason === "other";
  const confirmDisabled =
    anyPending ||
    path === null ||
    (path === "extend" && (!requestedDate || !reason.trim())) ||
    (closeNoteRequired && !closeNotes.trim());

  function handleConfirm() {
    if (confirmDisabled) return;
    setError(null);
    if (path === "extend") {
      // CHP-525: a proposed date on/before the current expiry is rejected by the
      // backend; guard here too (mirrors DealExtensionRequestAction) so a
      // typed/programmatic out-of-range date gets the reason without a round-trip.
      if (deal.target_close_date && requestedDate <= deal.target_close_date) {
        setError("The proposed date must be after the current expiry date.");
        return;
      }
      extend.mutate();
    } else if (path === "leave") acknowledge.mutate();
    else if (path === "close") close.mutate();
  }

  return (
    <div
      role="dialog"
      aria-labelledby="expiry-decision-heading"
      className="rounded-md border border-border bg-background p-5"
    >
      <h3 id="expiry-decision-heading" className="text-base font-semibold">
        Manage {deal.client_name} deal registration
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        This registration expires on {formatAuDate(deal.target_close_date)}.
      </p>

      {/* Review-r2 (ecb3619c): selectable option rows on the design-system
          surface (Card-style border + accent ring on selection + lead icon),
          replacing the bare native radio list that read as "generic". */}
      <fieldset className="mt-4 grid gap-2" disabled={anyPending}>
        <PathOption
          icon={CalendarClock}
          label="Request an extension"
          hint="Push the registration expiry further out."
          name="expiry-path"
          selected={path === "extend"}
          onSelect={() => {
            setPath("extend");
            setError(null);
          }}
        />
        <PathOption
          icon={CheckCircle2}
          label="Let it expire"
          hint="Keep protection until the current expiry date, then let it lapse."
          name="expiry-path"
          selected={path === "leave"}
          onSelect={() => {
            setPath("leave");
            setError(null);
          }}
        />
        <PathOption
          icon={XCircle}
          label="Close deal now"
          hint="End the registration immediately and record a reason."
          name="expiry-path"
          selected={path === "close"}
          onSelect={() => {
            setPath("close");
            setError(null);
          }}
        />
      </fieldset>

      {/* Path 1: Request an extension — shared fields (DealExtensionRequestForm) */}
      {path === "extend" && (
        <div className="mt-4 rounded border border-border p-4">
          <DealExtensionFields
            requestedDate={requestedDate}
            onRequestedDateChange={setRequestedDate}
            justification={reason}
            onJustificationChange={setReason}
            minDate={minExtensionDate(deal)}
            // Review-r2 (5541d74c): extension capped at current expiry + 90 days.
            max={maxExtensionDate(deal.target_close_date)}
          />
        </div>
      )}

      {/* Path 2: Let it expire */}
      {path === "leave" && (
        <div className="mt-4 rounded border border-warning-border bg-warning-muted p-4 text-warning-muted-foreground">
          <p className="text-sm">
            Registration expires on{" "}
            <span className="font-semibold">{formatAuDate(deal.target_close_date)}</span>. Deal
            protection remains active until that date.
          </p>
        </div>
      )}

      {/* Path 3: Close now */}
      {path === "close" && (
        <div className="mt-4 space-y-3 rounded border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm text-muted-foreground">
            This deal registration ceases upon confirmation and will no longer be active.
          </p>
          <div className="space-y-1">
            <Label htmlFor="close-reason">Reason for closure *</Label>
            <Select
              value={closeReason}
              onValueChange={(v) => setCloseReason(v as CloseReasonCategory)}
            >
              <SelectTrigger id="close-reason" className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLOSE_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {CLOSE_REASON_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="close-notes">
              Additional details{closeNoteRequired ? " *" : " (Optional)"}
            </Label>
            <Textarea
              id="close-notes"
              value={closeNotes}
              onChange={(e) => setCloseNotes(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder={
                closeNoteRequired
                  ? "Required — briefly describe the reason for closing."
                  : undefined
              }
              className="w-full"
            />
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          loading={anyPending}
          loadingLabel="Updating…"
        >
          Confirm
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={anyPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** Review-r2 (ecb3619c): a design-system selectable option row — a bordered
 * card-style label with a lead icon, a hidden native radio for accessibility,
 * and an accent ring when selected. Replaces the bare native radio list. */
function PathOption({
  icon: Icon,
  label,
  hint,
  name,
  selected,
  onSelect,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint: string;
  name: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-xl border border-input bg-card p-3 transition-colors hover:bg-muted/50",
        selected && "border-primary ring-1 ring-primary",
      )}
    >
      <input type="radio" name={name} checked={selected} onChange={onSelect} className="sr-only" />
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          selected ? "text-primary" : "text-muted-foreground",
        )}
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{label}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}
